import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_V1, fetchApi, getImageUrl } from '../config/api';
import { useCart } from '../context/CartContext';
import { getExtraCartId } from '../utils/cartExtraId';
import { fallbackImageForExtra, isRestaurantExtra } from '../utils/extraImages';
import { refAmount } from '../utils/money';
import { isWasabiGingerExtra } from '../constants/wasabiGinger';
import './CombosList.css';
import rollDinamitaImage from '../assets/Tipos de Rolls/ROLL Camaron - Dinamita.png';

const SORT_PRICE_ASC = 'price_asc';
const SORT_PRICE_DESC = 'price_desc';
const SORT_NAME_ASC = 'name_asc';
const SORT_NAME_DESC = 'name_desc';

function extraImage(extra) {
  return (
    getImageUrl(extra.image_url ?? extra.image ?? extra.thumbnail) ||
    fallbackImageForExtra(extra.title ?? extra.name) ||
    rollDinamitaImage
  );
}

const ExtrasList = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('');

  useEffect(() => {
    const fetchExtras = async () => {
      try {
        setError(null);
        const params = new URLSearchParams({
          per_page: '100',
          is_active: '1',
        });
        const res = await fetchApi(`${API_V1}/extras?${params.toString()}`);
        if (!res.ok) throw new Error('Error al cargar extras');
        const json = await res.json();
        const list = json.data ?? json;
        const raw = Array.isArray(list) ? list : [];
        setExtras(
          raw
            .filter(isRestaurantExtra)
            .map((extra) => ({
              id: extra.id ?? extra.extra_id,
              name: extra.title ?? extra.name ?? 'Extra',
              description: extra.description || '',
              price: refAmount(extra.price_eur),
              price_eur: Number(extra.price_eur) || 0,
              image: extraImage(extra),
              xetux_product_id: extra.xetux_product_id ?? null,
              xetux_item_id: extra.xetux_item_id ?? null,
              requiresFlavor: Boolean(extra.requires_flavor),
              flavors: Array.isArray(extra.flavors) ? extra.flavors : [],
            }))
            .filter((extra) => extra.id != null)
        );
      } catch (err) {
        setError(err.message);
        setExtras([]);
      } finally {
        setLoading(false);
      }
    };
    fetchExtras();
  }, []);

  const sortedExtras = useMemo(() => {
    const list = [...extras];
    if (sortBy === SORT_PRICE_ASC) list.sort((a, b) => a.price_eur - b.price_eur);
    if (sortBy === SORT_PRICE_DESC) list.sort((a, b) => b.price_eur - a.price_eur);
    if (sortBy === SORT_NAME_ASC) list.sort((a, b) => a.name.localeCompare(b.name, 'es'));
    if (sortBy === SORT_NAME_DESC) list.sort((a, b) => b.name.localeCompare(a.name, 'es'));
    return list;
  }, [extras, sortBy]);

  const priceLabel = (extra) => {
    const prices = (extra.flavors || [])
      .map((flavor) => Number(flavor.price))
      .filter((price) => !Number.isNaN(price));
    if (extra.requiresFlavor && prices.length) {
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      const formatted = min.toFixed(2);
      return min === max ? formatted : `Desde ${formatted}`;
    }
    return extra.price;
  };

  const handleAdd = (extra, event) => {
    event.preventDefault();
    event.stopPropagation();
    if (extra.requiresFlavor || isWasabiGingerExtra(extra.name)) {
      navigate(`/extra/${extra.id}`);
      return;
    }
    addToCart({
      id: getExtraCartId('catalog', 'extras', extra.id, extra.name),
      name: extra.name,
      price: extra.price,
      quantity: 1,
      image: extra.image,
      type: 'extra',
      extraId: extra.id,
      xetuxProductId: extra.xetux_product_id,
      xetuxItemId: extra.xetux_item_id,
    });
  };

  return (
    <section className="combos-list">
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Inicio</Link>
          <span> / </span>
          <Link to="/categorias">CATEGORÍAS</Link>
          <span> / </span>
          <span>Extras</span>
        </div>

        <h1 className="combos-title">EXTRAS</h1>

        {error && (
          <p className="combos-error">No se pudieron cargar los extras. Verifica que la API esté disponible.</p>
        )}
        {loading && <p className="combos-loading">Cargando extras...</p>}

        <div className="combos-header">
          <div className="combos-filters">
            <div className="combos-count">
              <span>{sortedExtras.length} extras</span>
            </div>
          </div>
          <div className="combos-sort">
            <label>Ordenar por:</label>
            <select
              className="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="">Seleccionar</option>
              <option value={SORT_PRICE_ASC}>Precio: menor a mayor</option>
              <option value={SORT_PRICE_DESC}>Precio: mayor a menor</option>
              <option value={SORT_NAME_ASC}>Nombre A-Z</option>
              <option value={SORT_NAME_DESC}>Nombre Z-A</option>
            </select>
          </div>
        </div>

        <div className="combos-grid">
          {!loading && !error && sortedExtras.map((extra) => (
            <article key={extra.id} className="combo-card">
              <div
                className="combo-image-container"
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/extra/${extra.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(`/extra/${extra.id}`);
                }}
              >
                <img
                  src={extra.image}
                  alt={extra.name}
                  className="combo-image"
                  onError={(e) => {
                    const fallback = fallbackImageForExtra(extra.name);
                    if (fallback && e.currentTarget.src !== fallback) {
                      e.currentTarget.src = fallback;
                      return;
                    }
                    e.currentTarget.src = rollDinamitaImage;
                  }}
                />
              </div>
              <div className="combo-content">
                <span className="combo-ref">Ref {priceLabel(extra)}</span>
                <h3 className="combo-name">{extra.name}</h3>
                {extra.description ? (
                  <p className="combo-category">{extra.description}</p>
                ) : null}
                <button
                  type="button"
                  className="add-to-cart-btn extra-card-btn"
                  onClick={(e) => handleAdd(extra, e)}
                >
                  {extra.requiresFlavor
                    ? 'Elegir sabor'
                    : isWasabiGingerExtra(extra.name)
                      ? 'Elegir combinación'
                      : 'Añadir al carrito'}
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExtrasList;
